'use client';

import { useState } from 'react';
import { Box, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { moonpayApi } from '@/services/api/moonpay.api';
import { DepositHeader } from './subcomponents/deposit';

/**
 * HOSTED deposit (non-headless countries). MoonPay owns the ENTIRE experience —
 * KYC, payment method, currency — inside one signed widget iframe. We only pass
 * the amount + local fiat as a prefill; there is no Customer-API/frames path here
 * and no fallback into the headless flow.
 */

type Step = 'amount' | 'widget' | 'error';

interface Props {
  /** ISO alpha-2 residence country. */
  country: string;
  /** Local fiat for the country (ISO 4217). */
  fiat: string;
  onBack: () => void;
  onClose: () => void;
  balance?: number;
}

const QUICK = [200, 500, 1000];
const inputCls =
  'w-full border-none bg-transparent p-0 text-3xl font-bold text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]';

export const MoonpayHostedDeposit = ({ fiat, onBack, onClose, balance = 0 }: Props) => {
  const { t } = useT();

  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState<string>('');
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountNum = Number(amount);
  const canContinue = amountNum > 0;

  const openWidget = async () => {
    setError(null);
    setLoading(true);
    try {
      const { url: signedUrl } = await moonpayApi.getHostedBuyUrl({ amount: amountNum, fiat });
      setUrl(signedUrl);
      setStep('widget');
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not open MoonPay');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="flex min-h-[520px] flex-col">
      <DepositHeader
        balance={balance}
        onClose={onClose}
        onBack={step === 'amount' ? onBack : () => setStep('amount')}
        title={t('moonpay.title', { defaultValue: 'Deposit with MoonPay' })}
      />

      {step === 'amount' && (
        <Box className="flex flex-1 flex-col gap-4">
          <Box className="flex flex-col gap-2 rounded-[24px] bg-[var(--bg-secondary)] p-6 dark:bg-[#F8F9FB10]">
            <Typography className="text-sm font-medium text-[var(--text-muted)]">
              {t('moonpay.youSpend', { defaultValue: 'You spend' })}
            </Typography>
            <Box className="flex items-center gap-1">
              <input
                className={inputCls}
                inputMode="decimal"
                placeholder="0"
                value={amount}
                onChange={(e) => /^\d*\.?\d*$/.test(e.target.value) && setAmount(e.target.value)}
              />
              <Typography className="text-sm font-bold text-[var(--text-muted)]">{fiat}</Typography>
            </Box>
          </Box>
          <Box className="flex gap-2">
            {QUICK.map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                className="flex-1 rounded-xl border border-[var(--border-color)] py-2 text-sm font-bold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-secondary)]"
              >
                {v}
              </button>
            ))}
          </Box>
          <Typography className="text-xs text-[var(--text-muted)]">
            {t('moonpay.hostedNote', {
              defaultValue: 'You’ll complete verification and payment securely with MoonPay.',
            })}
          </Typography>
          <Box className="mt-auto">
            <button
              disabled={!canContinue || loading}
              onClick={openWidget}
              className="w-full rounded-2xl bg-[var(--primary)] px-4 py-3.5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading
                ? t('moonpay.opening', { defaultValue: 'Opening…' })
                : t('common.continue', { defaultValue: 'Continue' })}
            </button>
          </Box>
        </Box>
      )}

      {step === 'widget' && url && (
        <Box className="flex flex-1 flex-col">
          <iframe
            title="MoonPay"
            src={url}
            allow="accelerometer; autoplay; camera; gyroscope; payment; microphone"
            className="min-h-[560px] w-full flex-1 rounded-2xl border-none bg-white"
          />
          <button
            onClick={onClose}
            className="mt-3 w-full rounded-2xl border border-[var(--border-color)] px-4 py-3 text-sm font-bold text-[var(--text-primary)]"
          >
            {t('common.done', { defaultValue: 'Done' })}
          </button>
        </Box>
      )}

      {step === 'error' && (
        <Box className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <Box className="text-3xl">⚠️</Box>
          <Typography className="max-w-xs text-sm text-[var(--accent-red)]">{error}</Typography>
          <button
            onClick={() => {
              setError(null);
              setStep('amount');
            }}
            className="rounded-2xl border border-[var(--border-color)] px-6 py-3 text-sm font-bold text-[var(--text-primary)]"
          >
            {t('common.tryAgain', { defaultValue: 'Try again' })}
          </button>
        </Box>
      )}
    </Box>
  );
};
